import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Bell, MapPin, Menu, Moon, Search, Sun } from 'lucide-react';
import { useNotifications } from '@/context/NotificationContext';
import { useTheme } from '@/context/ThemeContext';
import { useHasShops } from '@/hooks/useAppData';
import { useCatalogPath } from '@/hooks/useRouteLocale';
import UserMenu from '@/components/layout/UserMenu';
import CountrySelector from '@/components/layout/CountrySelector';

interface HeaderProps {
  /** Opens the MobileDrawer (state lives in AppShell). */
  onOpenDrawer: () => void;
}

/** Catalog pages as un-prefixed paths, linked through `useCatalogPath` (web plan W2.1). */
const NAV_LINKS: { to: string; label: string; end?: boolean }[] = [
  { to: '/', label: 'Home', end: true },
  { to: '/brands', label: 'Brands' },
  { to: '/shops', label: 'Shops' },
  { to: '/promotions', label: 'Promotions' },
];

/** `/shops` is linked only while it has shops to list (web plan W0.3). */
function primaryNavLinks(hasShops: boolean): typeof NAV_LINKS {
  return hasShops ? NAV_LINKS : NAV_LINKS.filter((link) => link.to !== '/shops');
}

const ICON_BTN =
  'relative flex h-10 w-10 items-center justify-center rounded-full text-ink-secondary transition-all duration-200 hover:bg-line-light hover:text-ink';

function UnreadBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span className="absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-white">
      {count > 99 ? '99+' : count}
    </span>
  );
}

export function Header({ onOpenDrawer }: HeaderProps) {
  const navigate = useNavigate();
  const { unreadCount } = useNotifications();
  const { resolved, toggle } = useTheme();
  const catalog = useCatalogPath();
  const navLinks = primaryNavLinks(useHasShops());
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  // Global "/" shortcut focuses the search input.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable)
      ) {
        return;
      }
      e.preventDefault();
      searchRef.current?.focus();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const handleSearchSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const q = query.trim();
    navigate(catalog(q ? `/search?query=${encodeURIComponent(q)}` : '/search'));
    setQuery('');
    searchRef.current?.blur();
  };

  return (
    <header className="sticky top-0 z-40 border-b border-line bg-background/80 backdrop-blur-lg">
      <div className="mx-auto flex h-16 max-w-7xl items-center gap-4 px-4">
        {/* Logo */}
        <Link to={catalog('/')} className="flex shrink-0 items-center gap-2" aria-label="PromoStork home">
          {/* 72 px (2× the 36 px box), ~3 KB — not the 256 px, 66 KB logo.png. */}
          <img src="/logo-72.jpg" alt="" width="36" height="36" decoding="async" className="h-9 w-9 rounded-xl object-cover" />
          <span className="hidden text-lg font-extrabold tracking-tight sm:block">
            <span className="text-ink">Promo</span>
            <span className="text-primary-strong">Stork</span>
          </span>
        </Link>

        {/* Primary nav */}
        <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary">
          {navLinks.map((link) => (
            <NavLink
              key={link.to}
              to={catalog(link.to)}
              end={link.end}
              className={({ isActive }) =>
                `rounded-full px-4 py-2 text-sm font-semibold transition-all duration-200 ${
                  isActive
                    ? 'bg-primary/10 text-primary-strong'
                    : 'text-ink-secondary hover:bg-line-light hover:text-ink'
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        {/* Global search */}
        <form
          onSubmit={handleSearchSubmit}
          className="hidden max-w-md flex-1 md:block"
          role="search"
        >
          <div className="relative">
            <Search
              size={16}
              className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-tertiary"
            />
            <input
              ref={searchRef}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search brands, shops, promotions..."
              aria-label="Search brands, shops, promotions"
              className="h-10 w-full rounded-full border border-line bg-surface pl-10 pr-12 text-sm text-ink transition-all duration-200 placeholder:text-ink-tertiary"
            />
            <kbd className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 rounded border border-line px-1.5 text-[10px] text-ink-tertiary">
              /
            </kbd>
          </div>
        </form>

        {/* Spacer when search is hidden so actions stay right-aligned */}
        <div className="flex-1 md:hidden" />

        {/* Actions */}
        <div className="flex shrink-0 items-center gap-1.5">
          <Link to={catalog('/search')} className={`${ICON_BTN} md:hidden`} aria-label="Search">
            <Search size={20} />
          </Link>
          <Link to={catalog('/nearby')} className={ICON_BTN} title="Nearby" aria-label="Nearby stores and shops">
            <MapPin size={20} />
          </Link>
          <Link to="/notifications" className={ICON_BTN} aria-label="Notifications">
            <Bell size={20} />
            <UnreadBadge count={unreadCount} />
          </Link>
          <button
            type="button"
            onClick={toggle}
            className={ICON_BTN}
            aria-label={resolved === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {resolved === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
          </button>
          <CountrySelector />
          {/* The auth slot: one fixed 36 × 36 px box for the guest icon and
              the signed-in avatar alike. A server-rendered page shows the
              guest until hydration hands over the stored session (web plan
              W4.2), and nothing in the header may move when it does. */}
          <div className="hidden h-9 w-9 shrink-0 md:block" data-auth-slot="">
            <UserMenu />
          </div>
          <button
            type="button"
            onClick={onOpenDrawer}
            className={`${ICON_BTN} lg:hidden`}
            aria-label="Open menu"
          >
            <Menu size={20} />
          </button>
        </div>
      </div>
    </header>
  );
}

export default Header;
